import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Avatar } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { InputText } from 'primeng/inputtext';
import { Menu } from 'primeng/menu';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';
import { FeatureNameInterface } from 'src/app/classes/_interfaces/access control/access-contol.interface';
import { SiteService } from 'src/app/classes/_services/site.service';
import { AccessControlStore } from 'src/app/classes/stores/access-control-store';
import packageJson from '../../../../../package.json';
import { environment } from '../../../../environments/environment';

import { SensorAttributes } from 'src/app/classes/sensor-attributes';
import { Site } from 'src/app/classes/site';
import { AppIconComponent } from 'src/app/shared-components/app-icon/app-icon.component';
import { NavMenuChild } from './nav-menu-child.interface';

@Component({
  selector: 'app-navbar-vertical',
  standalone: true,
  imports: [
    CommonModule,
    Tooltip,
    RouterLink,
    RouterLinkActive,
    Menu,
    Avatar,
    InputText,
    Popover,
    FormsModule,
    InputGroup,
    InputGroupAddon,
    ButtonModule,
    AppIconComponent
  ],
  templateUrl: './navbar-vertical.component.html',
  styleUrls: ['./navbar-vertical.component.scss'],
  animations: [
    trigger('submenu', [
      state(
        'closed',
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden',
          padding: '0'
        })
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: 1,
          overflow: 'hidden'
        })
      ),
      transition('closed <=> open', animate('200ms ease-in-out'))
    ])
  ]
})
export class NavbarVerticalComponent {
  @ViewChild('userDropdown') public userDropdownMenu!: Menu;
  @ViewChild('op') public overlayReference;
  @ViewChildren('row') public rowElements!: QueryList<ElementRef<HTMLDivElement>>;

  public collapsed = true;
  public version: string = packageJson.version;
  public deploymentName: string = packageJson.deploymentName;
  public shortVersionString = 'v' + this.version;
  public longVersionString = this.shortVersionString + ' - ' + this.deploymentName;
  public userImage = 'assets/Brian.jpeg';
  private fallbackImage = 'assets/default-avatar.png';
  private readonly showCSADashboard = environment.showCsaDashboard;
  private readonly destroyRef = inject(DestroyRef);

  public menuItems = [
    {
      label: 'Live Alerts',
      icon: 'light-emergency-on',
      children: [
        {
          label: 'Overview',
          icon: 'map',
          route: '/live-alerts',
          ariaLabel: 'Navigate to Live Alerts Overview'
        },
        {
          label: 'Triage',
          icon: 'table-cells',
          route: '/triage-view',
          ariaLabel: 'Navigate to Live Alerts Triage'
        }
      ]
    },
    {
      label: 'Dashboards',
      icon: 'gauge',
      children: [
        {
          label: 'Pumps',
          icon: 'pump',
          route: '/pump-dashboard',
          ariaLabel: 'Navigate to Pumps Dashboard'
        },
        {
          label: 'Alert Overview',
          icon: 'bell-on',
          route: '/alert-overview-dashboard',
          ariaLabel: 'Navigate to Alert Overview Dashboard'
        },
        {
          label: 'Sensor Health',
          icon: 'sensor-on',
          route: '/sensor-health',
          ariaLabel: 'Navigate to Sensor Health Dashboard'
        }
      ]
    },
    {
      label: 'Spillage Predictions',
      icon: 'fill-drip',
      route: '/spill-preds',
      ariaLabel: 'Navigate to Spillage Predictions Page'
    },
    {
      label: 'CSAs',
      icon: 'pipe-collar',
      route: '/csa-dashboard',
      ariaLabel: 'Navigate to CSAs Page',
      hidden: !this.showCSADashboard
    },
    {
      label: 'Map View',
      icon: 'map',
      route: '/map-view',
      ariaLabel: 'Navigate to Map View Page'
    },
    {
      label: 'Network Performance',
      icon: 'chart-network',
      route: '/network-performance',
      ariaLabel: 'Navigate to Network Performance Page'
    },
    {
      label: 'Sites',
      icon: 'list-ol',
      children: [
        {
          label: 'All Sites',
          icon: 'globe',
          route: '/all-sites',
          ariaLabel: 'Navigate to All Sites Page'
        },
        {
          label: 'Treatment Works',
          icon: 'industry',
          route: '/treatment-works',
          ariaLabel: 'Navigate to Treatment Works Page'
        }
      ]
    }
  ];

  public userMenu = [
    { label: 'Sign Out', icon: 'pi pi-sign-out', routerLink: '/logout' },
    { label: 'Change Password', icon: 'pi pi-lock', routerLink: '/change-password' },
    {
      label: 'Privacy Policy',
      icon: 'pi pi-file-o',
      command: () => {
        window.open(
          'https://stormharvester.com/product-privacy-policy/',
          '_blank',
          'noopener,noreferrer'
        );
      }
    }
  ];

  public submenuStates: Record<string, boolean> = {};
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);
  private sitesService = inject(SiteService);
  private accessControlStore = inject(AccessControlStore);
  private isMobileView = false;
  public searchText = '';
  public sites: Site[] = [];
  public results: Site[] = [];
  public activeIndex = -1;
  public loadingSites: boolean;

  /** Re-evaluates when access-control `features` loads or updates (constructor alone ran too early). */
  public readonly showAdmin = computed(() =>
    this.accessControlStore.hasWritePermission(FeatureNameInterface.USER_MANAGEMENT)
  );

  constructor() {
    this.observeBreakpoints();
    this.loadingSites = true;
    if (!environment.hasWWTWFacilityPage) {
      this.menuItems.find(i => i.label === 'Sites').children.splice(1, 1);
    }
    this.sitesService
      .fetchSites()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (sites: Site[]) => {
          this.sites = sites;
          if (sites.length > 0) {
            this.loadingSites = false;
          }
        },
        error: err => {
          console.error('Error fetching sites:', err);
          this.loadingSites = false;
        }
      });
  }

  public toggle(): void {
    this.collapsed = !this.collapsed;
  }

  public openUserMenu(event: MouseEvent): void {
    this.userDropdownMenu.toggle(event);
  }

  public toggleSubmenu(label: string): void {
    this.submenuStates[label] = !this.submenuStates[label];
  }

  public submenuPanelId(label: string): string {
    return 'nav-submenu-' + label.replace(/\s+/g, '-');
  }

  public isChildRouteActive(children: NavMenuChild[]): boolean {
    return children.some(child =>
      this.router.isActive(child.route, {
        paths: 'subset',
        queryParams: 'subset',
        fragment: 'ignored',
        matrixParams: 'ignored'
      })
    );
  }

  public useFallbackAvatar() {
    this.userImage = this.fallbackImage;
  }

  private observeBreakpoints(): void {
    this.breakpointObserver.observe([Breakpoints.Handset]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      this.isMobileView = result.matches;

      if (this.isMobileView) {
        this.collapsed = true;
      }
    });
  }

  public onNavLinkClick(): void {
    if (this.isMobileView) {
      this.collapsed = true;
      this.submenuStates = {};
    }
  }

  public onSearchLinkClick(): void {
    this.collapsed = !this.collapsed;
  }

  public search(event: Event): void {
    const inputElement: HTMLInputElement = event.target as HTMLInputElement;
    const searchString: string = inputElement.value;
    this.overlayReference.show(event);
    const results = [];
    const searchLower: string = searchString.toLowerCase().trim();

    if (searchString.length) {
      for (const site of this.sites) {
        const matchesDisplayName = site.display_name?.toLowerCase().includes(searchLower);
        const matchesId = site.id && site.id.toLowerCase().includes(searchLower);
        const matchesSensorId = this.siteMatchesSensorId(site, searchLower);

        const matches =
          matchesDisplayName ||
          matchesSensorId ||
          (environment.customerIdentifier !== 'AW' && matchesId);

        if (matches) {
          results.push(site);
          if (results.length === 10) break;
        }
      }
    } else {
      this.overlayReference.hide();
      this.activeIndex = -1;
    }
    this.results = results;
  }

  private siteMatchesSensorId(site: Site, searchLower: string): boolean {
    return (
      (site.sensorAttributes?.some((s: SensorAttributes) =>
        (s?.id ?? '').toLowerCase().includes(searchLower)
      ) ||
        site.digitalSensorAttributes?.some(s =>
          (s?.id ?? '').toLowerCase().includes(searchLower)
        )) ??
      false
    );
  }

  public navigateToSite(site: Site): void {
    this.collapsed = true;
    this.clearSearch();
    // Navigate to site, using facility to create correct URL
    // Store site in sessionStorage to grab in dashboard - eliminates an extra API call
    const url: string = this.generateRouterLink(site);
    sessionStorage.setItem('currentSite', JSON.stringify(site));
    this.router.navigate([url]);
  }

  private generateRouterLink(site: Site): string {
    return `/site${site.displayFacility}/${site.name}`;
  }

  public focusFunction(event: FocusEvent): void {
    if (this.searchText && this.searchText.length > 0) {
      this.search(event);
    }
  }

  public clearSearch(): void {
    this.searchText = '';
    this.overlayReference.hide();
    this.activeIndex = -1;
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (!this.results || this.results.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % this.results.length;
        this.scrollToActive();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length;
        this.scrollToActive();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.activeIndex >= 0 && this.activeIndex < this.results.length) {
          this.navigateToSite(this.results[this.activeIndex]);
        }
        break;

      default:
        break;
    }
  }

  public scrollToActive(): void {
    if (this.activeIndex >= 0 && this.rowElements && this.rowElements.length > this.activeIndex) {
      const el: ElementRef<HTMLDivElement> = this.rowElements.toArray()[this.activeIndex];
      el.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }
}
